import React, { useState, useEffect } from 'react';
import cn from 'clsx';
import { useHistory } from 'react-router-dom';

import { Controller, useFormContext } from 'react-hook-form';
import { CreatePollData } from './types';
import { addMonths } from 'date-fns';
import { Colors } from '@theme/index';
import Toggle from '@components/elements/Toggle';
import ButtonMighty from '@components/elements/ButtonMighty';
import BlockImagePicker from '@components/elements/BlockImagePicker';
import Card from '@components/elements/Card';
import InputTextarea from '@components/elements/InputTextarea';
import InputText from '@components/elements/InputText';
import PollPeriodInput from '@components/modules/Poll/PeriodInput';
import { WarningIcon } from '@components/elements/Icons';
import Loader from '@components/elements/Loader';
import ConfirmDialog from '@components/elements/ConfirmDialog';
import { useAuth } from '@hooks/Auth';
import { CompanyRole, Tier } from '@sdk/API';
import { LOCATION_LIST } from '@sdk/managers/UsersManager';
import { canUserCreatePremiumPoll } from '@sdk/managers/PollsManager';
import { useGlobalContext } from '@context/customContext';
import { mergeDateTime } from '@utils/Date';
import { addMinutes, isAfter } from 'date-fns';
import PollPremiumQuotaForm from '@components/modules/Poll/PollPremiumQuotaForm';
import toast from 'react-hot-toast';
import useSwr from 'swr';
import { PollDetail } from '@utils/Poll';
import { makeStyles } from '@mui/styles';
import { Multiselect } from 'multiselect-react-dropdown';
import { chatGpt } from '@sdk/managers/IdeaManager';
const useStyles = makeStyles({
  cbutton: {
    border: 'solid 2px #2EFFE4',
    '&:hover': {
      border: 'solid 2px transparent',
    },
  },
});

interface PollCreateStepOneProps {
  loading?: boolean;
  isSubmitting?: boolean;
  isEdit: boolean;
  isPledge?: boolean;
  onNext: (submit?: boolean) => void;
  onDelete: () => void;
  end: () => void;
  pollData?: PollDetail;
  neon: string;
  StepTwo: any;
  categories: any;
  hashtags: any;
  r: boolean;
}

const dropdownCn =
  'bg-transparent py-3 px-2 w-full font-display text-sm outline-none placeholder-opacity-80 focus:placeholder-opacity-40 text-white text-opacity-100 focus:text-opacity-80 placeholder-purple';

const PollCreateStepOne: React.FC<PollCreateStepOneProps> = ({
  loading = false,
  isSubmitting = false,
  isPledge = false,
  isEdit,
  onDelete,
  onNext,
  end,
  pollData,
  neon,
  categories,
  r,
  hashtags,
  StepTwo,
}) => {
  const { dbUser, company, refetch: refetchUser } = useAuth();
  const history = useHistory<any>();
  const classes = useStyles();
  React.useEffect(() => {
    refetchUser();
  }, [refetchUser]);

  const {
    control,
    register,
    formState: { errors },
    reset,
    setValue,
    getValues,
    watch,
  } = useFormContext<CreatePollData>();
  const { customTheme } = useGlobalContext();

  const formPremium = watch('isPremium');
  const formCompany = watch('company');
  const formVisibility = watch('private');
  const [purchaseModal, setPurchaseModal] = useState<{
    visible: boolean;
    isSubmitting: boolean;
    onConfirm?: () => any;
    onCancel?: () => any;
  }>({
    isSubmitting: false,
    visible: false,
  });

  const { data: hasPremium } = useSwr(
    dbUser?.id ? ['/user/hasPremium', dbUser?.id] : null,
    async () => {
      if (!dbUser?.id) {
        return;
      }

      const res: any = await canUserCreatePremiumPoll(dbUser?.id);
      return res;
    },
    {
      revalidateOnFocus: false,
      onError: (err, key) => {
        console.log(
          'Failed to fetch if user can create premium polls',
          { key },
          err
        );
      },
    }
  );

  const onPurchasePremium = React.useCallback(async () => {
    const sendPurchase = async () => {
      try {
        if (!dbUser) throw new Error('Not logged in');

        // console.log('onPurchasePremium', { res });
        await Promise.all([refetchUser()]);
      } catch (err: any) {
        toast.error('Failed to purchase poll ', err.message);
      }
    };

    setPurchaseModal({
      visible: true,
      isSubmitting: false,
      onConfirm: async () => {
        await sendPurchase();
        setPurchaseModal({
          isSubmitting: false,
          visible: false,
        });
      },
      onCancel: () => {
        setPurchaseModal({
          isSubmitting: false,
          visible: false,
        });
      },
    });
  }, [dbUser, refetchUser]);

  const premiumPolls = dbUser?.premiumPolls || 0;
  const usedPremiumPolls = dbUser?.usedPremiumPolls || 0;
  const pollsRemaining = premiumPolls - usedPremiumPolls;

  let hasPremiumPlan = dbUser?.tier === Tier.ENTERPRISE;
  const [searchLoading, setSearchLoading] = useState(false);
  const searchChatasync = async (value: string) => {
    if (!value || searchLoading) return;
    setSearchLoading(true);
    try {
      const { data } = (await chatGpt(
        value +
          ' Use less than 120 characters to make the headline more attactive.'
      )) as any;
      const body = JSON.parse(data.result).body;
      reset({ ...getValues(), title: body });
    } finally {
      setSearchLoading(false);
      setGpt('');
    }
  };

  const [gpt, setGpt] = useState('');

  const onChangeGpt = (e: any) => {
    setGpt(e.target.value);
    // debounceChatGpt(e.target.value);
  };
  let selectThemePermission =
    (formPremium ||
      formCompany ||
      dbUser?.tier === Tier.ENTERPRISE ||
      dbUser?.tier === Tier.CONSULTATIVE) &&
    company;

  let pledgePermission =
    formPremium ||
    formCompany ||
    dbUser?.permissions?.pledgeAllowed ||
    dbUser?.tier === Tier.ENTERPRISE ||
    dbUser?.tier === Tier.CONSULTATIVE;

  // let passwordPermission =
  //   dbUser?.tier !== Tier.FREE && (formCompany && (company?.role === CompanyRole.ADMIN || company?.role === CompanyRole.REPRESENTATIVE))

  let groupPermission =
    formPremium ||
    formCompany ||
    dbUser?.permissions?.groupPollAllowed ||
    dbUser?.tier === Tier.ENTERPRISE ||
    dbUser?.tier === Tier.CONSULTATIVE;

  let content;
  let header;
  let leftContent;
  let deleteCard;
  if (loading)
    content = (
      <div className="flex flex-col items-center justify-center py-24 mx-8 text-center bg-white shadow-r-salmon-md">
        <Loader title="Loading your poll data..." />
      </div>
    );
  else {
    deleteCard = (
      <div className="mt-4">
        <p className="mb-4 px-4 text-sm text-salmon">
          This action is irreversible. Once you delete the poll, data and votes
          will not be recoverable.
        </p>
        <div className="flex justify-end">
          <button
            onClick={onDelete}
            style={{ background: 'red' }}
            className="flex items-center px-3 py-1 space-x-1 text-sm tracking-tight text-white font-display  hover:text-white"
          >
            <WarningIcon className="w-4 h-4" />
            <div>Delete</div>
          </button>
        </div>
      </div>
    );
    header = (
      <>
        {isEdit && <div className="lg:hidden">{deleteCard}</div>}
        <div className="mx-4">
          <h1 className="text-5xl " style={{ color: neon }}>
            {isEdit
              ? `Edit ${getValues('pledging') ? 'Pledge' : 'Poll'}`
              : `Create ${getValues('pledging') ? 'Pledge' : 'Poll'}`}
          </h1>
        </div>
      </>
    );
    leftContent = (
      <div className="px-4">
        <h2 className="text-4xl mt-4" style={{ color: neon, marginTop: 100 }}>
          {getValues('pledging') ? 'Pledge' : 'Poll'} management
        </h2>
        {isEdit && (
          <>
            <p
              className="mb-4 text-sm text-white w-full mt-8"
              style={{ color: 'red' }}
            >
              As your Poll is already created, any changes made to questions or
              period will result in the Poll being reset. All current votes will
              be lost.
            </p>

            <div className="grid n-col-2">
              <div className="hidden lg:flex">{deleteCard}</div>
              {mergeDateTime(
                new Date(pollData?.poll.endTime || '') || new Date(),
                new Date(getValues('period.endTime'))
              ) > new Date() &&
                mergeDateTime(
                  new Date(pollData?.poll.startTime || '') || new Date(),
                  new Date(getValues('period.startTime'))
                ) < new Date() && (
                  <div className="hidden lg:flex">
                    <Card
                      neon={neon}
                      bgColor="white"
                      borderColor="salmon"
                      shadowColor="red"
                      className=" mt-4 mb-8 lg:mb-0"
                    >
                      <p className="mb-4 px-4 text-sm text-salmon">
                        This action is irreversible. Once you end the poll now ,
                        data and votes will not be editable.
                      </p>
                      <div className="flex justify-end">
                        <button
                          onClick={end}
                          style={{ background: 'red' }}
                          className="flex items-center px-3 py-1 space-x-1 text-sm tracking-tight text-white font-display  hover:text-white"
                        >
                          <WarningIcon className="w-4 h-4" />
                          <div>End Poll Now</div>
                        </button>
                      </div>
                    </Card>
                  </div>
                )}
            </div>
          </>
        )}

        <div className="grid n-col-2">
          <Controller
            name="private"
            control={control}
            render={({ field, fieldState: { error: fieldError } }) => {
              return (
                <div
                  className="mt-6"
                  style={{
                    borderBottom: `solid 2px ${neon}`,
                  }}
                >
                  <Card
                    bgColor="white"
                    neon={neon}
                    className="p-4 space-y-4"
                    shadowColor="blue"
                  >
                    <h2 className="w-full mb-1 mr-4 text-2xl text-white">
                      Visibility
                    </h2>
                    <div>
                      <select
                        className={cn(dropdownCn)}
                        defaultValue={0}
                        onChange={field.onChange}
                        value={field.value}
                      >
                        <option className="capitalize text-black" value={0}>
                          Public
                        </option>
                        {formCompany && (
                          <option className="capitalize text-black" value={2}>
                            Enterprise
                          </option>
                        )}
                        <option className="capitalize text-black" value={1}>
                          Private
                        </option>
                      </select>
                      {fieldError && (
                        <p className="text-red">*{fieldError.message}</p>
                      )}
                    </div>
                  </Card>
                </div>
              );
            }}
          />
          {(company?.role === CompanyRole.ADMIN ||
            company?.role === CompanyRole.REPRESENTATIVE) && (
            <Controller
              name="company"
              control={control}
              render={({ field }) => (
                <div
                  className="mt-6"
                  style={{
                    borderBottom: `solid 2px ${neon}`,
                  }}
                >
                  <Card
                    bgColor="white"
                    neon={neon}
                    className="p-4 space-y-4"
                    shadowColor="blue"
                  >
                    <div style={{ height: 40 }} />
                    <select
                      className={cn(dropdownCn)}
                      defaultValue={0}
                      onChange={() => {
                        setValue('company', !getValues('company'));
                      }}
                      value={field.value ? 1 : 0}
                    >
                      <option className="capitalize text-black" value={0}>
                        Individual
                      </option>

                      <option className="capitalize text-black" value={1}>
                        Company
                      </option>
                    </select>
                  </Card>
                </div>
              )}
            />
          )}
        </div>
        <div className="grid n-col-2">
          {groupPermission && !getValues('toPledge') && (
            <Card
              bgColor="white"
              className="p-4"
              neon={neon}
              style={{
                borderBottom: '2px solid ' + neon,
              }}
            >
              <Controller
                name="group"
                control={control}
                render={({ field }) => (
                  <div className="flex flex-row w-full mb-2 items-between">
                    <h2 className="w-full mb-1 mr-4 text-2xl text-white">
                      Group Poll
                    </h2>
                    <div className="flex items-center">
                      <Toggle
                        {...field}
                        activeClassName="bg-salmon"
                        disabled={getValues('toPledge')}
                      />
                    </div>
                  </div>
                )}
              />
              <p className="text-sm text-white">
                Enable group poll to have multiple questions for your poll
              </p>
            </Card>
          )}
        </div>

        <div
          className="grid n-col-2"
          style={{ flexWrap: 'wrap', columnGap: 60 }}
        >
          {pledgePermission && !getValues('group') && !getValues('toPledge') && (
            <Controller
              name="pledging"
              control={control}
              render={({ field }) => (
                <>
                  <Card
                    bgColor="white"
                    className="p-4"
                    shadowColor="blue"
                    neon={neon}
                    style={{
                      borderBottom: '2px solid ' + neon,
                    }}
                  >
                    <div className="flex flex-row w-full mb-2 items-between">
                      <h2 className="w-full mb-1 mr-4 text-2xl text-white">
                        Pledging
                      </h2>
                      <div className="flex items-center">
                        <Toggle
                          {...field}
                          activeClassName="bg-blue"
                          disabled={getValues('toPledge')}
                        />
                      </div>
                    </div>
                    <p className="text-sm text-white">
                      Enable Pledging for your poll
                    </p>
                  </Card>
                </>
              )}
            />
          )}

          {pledgePermission && !isEdit && !getValues('group') && (
            <Controller
              name="toPledge"
              control={control}
              render={({ field }) => (
                <div
                  className={cn(
                    'flex flex-row px-4 py-4 my-6  bg-transparent items-between'
                  )}
                  style={{
                    borderBottom: '2px solid ' + neon,
                  }}
                >
                  <h3 className={cn('text-lg w-full ')} style={{ color: neon }}>
                    Direct To Pledge
                  </h3>
                  <div className="flex items-center">
                    <Toggle
                      {...field}
                      disabled={isPledge}
                      onChange={(value) => {
                        if (value) {
                          const d = new Date();
                          setValue('pledging', true);
                          setValue('group', false);
                          setValue('period.startDate', d);
                          setValue('period.endDate', d);
                          setValue('period.startTime', d);
                          setValue('period.endTime', d);
                          setValue('questions', [
                            {
                              question: '',
                              options: [
                                {
                                  title:
                                    getValues(`questions.${0}.question`) || '',
                                },
                              ],
                              maxVote: 1,
                              isNotExact: false,
                            },
                          ]);
                        } else {
                          setValue('questions', [
                            {
                              question: '',
                              options: [
                                {
                                  title: '',
                                },
                                {
                                  title: '',
                                },
                              ],
                              maxVote: 1,
                              isNotExact: false,
                            },
                          ]);
                          setValue('period.startDate', new Date());
                          setValue(
                            'period.endDate',
                            (dbUser?.tier || Tier.FREE) !== Tier.FREE
                              ? addMonths(new Date(), 1)
                              : addMinutes(new Date(), 23 * 60)
                          );
                          setValue(
                            'period.startTime',
                            addMinutes(new Date(), 10)
                          );

                          setValue('period.endTime', addMinutes(new Date(), 5));
                        }
                        reset({ ...getValues(), toPledge: value });
                        // field.onChange(value);
                      }}
                      activeClassName="bg-green"
                    />
                  </div>
                </div>
              )}
            />
          )}
        </div>
        {!getValues('toPledge') && (
          <div style={{ marginTop: 80 }}>
            <Controller
              name="period"
              control={control}
              render={({ field: { value, ...field } }) => (
                <PollPeriodInput
                  neon={neon}
                  isEdit={isEdit}
                  onChange={(values) => {
                    if (
                      getValues('period.immediate') !== values.immediate &&
                      (dbUser?.tier || Tier.FREE) === Tier.FREE &&
                      !getValues('isPremium') &&
                      values.immediate
                    ) {
                      values.startTime = new Date();
                      values.endTime = new Date();
                    } else if (
                      getValues('period.immediate') !== values.immediate &&
                      (dbUser?.tier || Tier.FREE) === Tier.FREE &&
                      !getValues('isPremium') &&
                      !values.immediate
                    ) {
                      values.startTime = addMinutes(new Date(), 10);
                      values.endTime = addMinutes(new Date(), 5);
                    }
                    const pledgeStartDateValue = getValues(
                      'pledgePeriod.startDate'
                    );
                    console.log(values);
                    if (pledgeStartDateValue) {
                      const pledgeStartDate = mergeDateTime(
                        new Date(pledgeStartDateValue),
                        new Date(getValues('pledgePeriod.startTime'))
                      );
                      const voteEndDate = mergeDateTime(
                        new Date(values.endDate),
                        new Date(values.endTime)
                      );

                      if (isAfter(voteEndDate, pledgeStartDate)) {
                        setValue('pledgePeriod.startDate', voteEndDate);
                        setValue(
                          'pledgePeriod.startTime',
                          addMinutes(voteEndDate, 1)
                        );
                      }
                    }
                    field.onChange(values);
                  }}
                  value={value}
                />
              )}
            />
          </div>
        )}
        {getValues('pledging') === true && (
          <Card
            bgColor="blue"
            shadowColor="magenta"
            neon={neon}
            style={{ color: neon, marginTop: 100 }}
          >
            <Controller
              name="pledgePeriod"
              control={control}
              render={({ field: { value, ...field } }) => (
                <PollPeriodInput
                  isPledge={true}
                  neon={neon}
                  isEdit={isEdit}
                  onChange={field.onChange}
                  value={value}
                  allowImmediate={getValues('toPledge')}
                />
              )}
            />
          </Card>
        )}
        <div className="grid n-col-2">
          <div className="my-6">
            {!formCompany && !hasPremiumPlan && (
              <Card
                neon={neon}
                bgColor="white"
                className="space-y-4"
                shadowColor="blue"
              >
                <h2 className="w-full mb-1 px-4 mr-4 text-2xl text-white">
                  Premium Poll ($0.99 SGD)
                </h2>
                <div>
                  <p className="mb-4 px-4 text-sm text-white">
                    {premiumPolls > 0 ? (
                      <>
                        Used quota {usedPremiumPolls}&nbsp;/&nbsp;
                        {premiumPolls}
                      </>
                    ) : (
                      <>Purchase premium polls to use full features</>
                    )}
                  </p>
                  {pollsRemaining <= 0 && (
                    <div className="flex justify-center">
                      <ButtonMighty
                        disabled={
                          loading || isSubmitting || purchaseModal.isSubmitting
                        }
                        neon={neon}
                        color="#29FF2E"
                        text="Purchase Premium Poll"
                        onClick={onPurchasePremium}
                        borderColor="#4400FF"
                        margin="0"
                        textColor="#4400FF"
                      />
                    </div>
                  )}
                </div>
                {pollsRemaining > 0 && (
                  <div>
                    <Controller
                      name="isPremium"
                      control={control}
                      render={({ field }) => (
                        <div
                          className={cn(
                            'flex flex-row px-4 py-4 my-6 bg-transparent items-between '
                          )}
                          style={{ borderBottom: `solid 2px ${neon}` }}
                        >
                          <h3
                            className={cn('mr-4 text-lg w-full text-white')}
                            style={{ color: field.value ? neon : 'white' }}
                          >
                            {field.value ? 'Premium' : 'Free'} Poll
                          </h3>
                          <div className="flex items-center">
                            <Toggle
                              {...field}
                              disabled={isEdit}
                              onChange={(value) => {
                                // If is currently private, set to unlisted
                                if (!value) setValue('toPledge', false);
                                if (value === false && formVisibility === '2') {
                                  setValue('private', '1');
                                }
                                field.onChange(value);
                              }}
                              activeClassName="bg-blue"
                            />
                          </div>
                        </div>
                      )}
                    />
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>
      </div>
    );
    content = (
      <div style={{ marginBottom: 100 }}>
        <Controller
          name="image"
          control={control}
          render={({ field: { value, onChange } }) => (
            <div className="overflow-hidden rounded-lg h-72 px-4">
              <BlockImagePicker files={value} onChange={onChange} length={1} />
            </div>
          )}
        />
        <h2 className="text-4xl mt-4 px-4" style={{ color: neon }}>
          {`About This ${getValues('pledging') ? 'Pledge' : 'Poll'} (${
            getValues('pledging') ? 'Pledge' : 'Poll'
          } Infomation)`}
        </h2>
        <div className="flex flex-col py-8 mx-4 space-y-8">
          <Card
            bgColor="white"
            shadowColor="salmon"
            borderColor="salmon"
            neon={neon}
          >
            <Controller
              name="title"
              control={control}
              render={({ field, fieldState: { error: fieldError } }) => {
                return (
                  <div className="relative">
                    <div
                      style={{ position: 'absolute', bottom: -20, right: 100 }}
                    >
                      {/* <ButtonMighty
                        color="#f97075"
                        neon={neon}
                        text={`REVISE TITLE WITH CHATGPT`}
                        loading={searchLoading}
                        disabled={searchLoading}
                        style={searchLoading ? { background: neon } : {}}
                        onClick={() => searchChatasync(field.value)}
                        borderColor="#221f20"
                        margin="0"
                        textColor={neon}
                        width="auto"
                      /> */}
                    </div>
                    <InputTextarea
                      required={true}
                      limitMax
                      className=" text-xl font-bold border-none"
                      placeholder={`Enter your ${
                        getValues('pledging') ? 'pledge' : 'poll'
                      } title*`}
                      neon={neon}
                      value={field.value}
                      max={120}
                      onChange={field.onChange}
                    />
                  </div>
                );
              }}
            />

            <Controller
              name="description"
              control={control}
              render={({ field, fieldState: { error: fieldError } }) => {
                return (
                  <InputTextarea
                    className="text-xl font-bold border-none mt-8"
                    placeholder={`Enter information about this  ${
                      getValues('pledging') ? 'pledge' : 'poll'
                    }`}
                    neon={neon}
                    value={field.value}
                    onChange={field.onChange}
                    rows={6}
                    max={1000}
                  />
                );
              }}
            />
            {/* <Controller
              name="description"
              control={form.control}
              render={({ field: { value, ...field } }) => (
                <InputTextarea
                  neon={true}
                  className="p-1"
                  rows={6}
                  max={1000}
                  value={value === null ? undefined : value}  
                  {...field}
                />
              )}
            /> */}
          </Card>
          <div
            className="grid n-col-2"

            // style={{
            //   display: 'grid grid-cols-3 gap-2',
            //   alignItems: 'center',
            //   justifyContent: 'space-between',
            // }}
          >
            {categories && categories.length && (
              <Controller
                name="category"
                control={control}
                render={({ field, fieldState: { error: fieldError } }) => {
                  return (
                    <div className="mt-6">
                      <h2 className="w-full mb-1 mr-4 text-2xl text-white">
                        Category
                      </h2>
                      <Card
                        bgColor="yellow"
                        shadowColor="salmon"
                        neon={neon}
                        style={{ borderBottom: `solid 2px ${neon}` }}
                      >
                        {/* <h2 className="w-full mb-1 mr-4 text-2xl text-gray-400">
                          Pledge category
                        </h2> */}
                        <div>
                          <select
                            className={cn(dropdownCn)}
                            onChange={field.onChange}
                            value={field.value}
                          >
                            {categories.map((item) => (
                              <option
                                key={item.title}
                                className="capitalize text-black"
                                value={item.title}
                              >
                                {item.title}
                              </option>
                            ))}
                            {/* <option className="capitalize text-black" value={0}>
                          Category1
                        </option>

                        <option className="capitalize text-black" value={1}>
                          Category2
                        </option> */}
                          </select>
                          {fieldError && (
                            <p className="text-red">*{fieldError.message}</p>
                          )}
                        </div>
                      </Card>
                    </div>
                  );
                }}
              />
            )}
            {hashtags && hashtags.length && r && (
              <Controller
                name="hashtag"
                control={control}
                render={({ field, fieldState: { error: fieldError } }) => {
                  return (
                    <div className="mt-6">
                      <Card
                        bgColor="yellow"
                        neon={neon}
                        shadowColor="salmon"
                        style={{
                          borderBottom: `solid 2px ${neon}`,
                          marginTop: -13.5,
                        }}
                      >
                        <div>
                          <h2 className="w-full mb-1 mr-4 text-2xl text-white">
                            Hashtag
                          </h2>

                          <Multiselect
                            style={{
                              multiselectContainer: {
                                background: 'transparent',
                                borderRadius: '0.375rem',
                                height: '49px',
                                marginBottom: 13.5,
                                color: 'white',
                              },
                              optionContainer: { color: 'black' },
                              option: {
                                // To change css for dropdown options
                                color: 'black',
                              },
                            }}
                            customArrow={<div></div>}
                            showArrow={true}
                            className={cn(dropdownCn)}
                            options={hashtags} // Options to display in the dropdown
                            selectedValues={getValues('hashtag')} // Preselected value to persist in dropdown
                            onSelect={(e) => {
                              setValue('hashtag', e);
                            }} // Function will trigger on select event
                            onRemove={(e) => {
                              setValue('hashtag', e);
                            }} // Function will trigger on remove event
                            displayValue="title" // Property name to display in the dropdown options
                          />

                          {fieldError && (
                            <p className="text-red">*{fieldError.message}</p>
                          )}
                        </div>
                      </Card>
                    </div>
                  );
                }}
              />
            )}
          </div>

          {/* <Card
            bgColor="yellow"
            shadowColor="salmon"
            className="p-4"
            neon={true}
          >
            <h2 className="mb-4 text-2xl text-white">
              Information About This Poll
            </h2>
            <Controller
              name="description"
              control={control}
              render={({ field: { value, ...field } }) => (
                <InputTextarea
                  className="p-1"
                  rows={6}
                  max={1000}
                  value={value === null ? undefined : value}
                  {...field}
                />
              )}
            />
          </Card> */}

          <Card bgColor="orange" shadowColor="green" neon={neon}>
            <h2 className="mb-4 text-2xl text-white">Embed Video Link</h2>
            <InputText
              neon={neon}
              type="text"
              borderColor={Colors['gray-darkest']}
              shadow={false}
              placeholder="youtube.com"
              {...register('video')}
            />
          </Card>

          {/* {selectThemePermission && (
            <Card
              bgColor="blue"
              shadowColor="magenta"
              className="p-4"
              neon={neon}
            >
              <h2 className="w-full mb-1 mr-4 text-2xl" style={{ color: neon }}>
                Select Theme
              </h2>
              <div>
                <select
                  className={cn(dropdownCn)}
                  defaultValue="default"
                  {...register('themeID')}
                >
                  <option className="capitalize text-black" value={'default'}>
                    Default Theme
                  </option>
                  <option className="capitalize text-black" value={'kids'}>
                    Kids
                  </option>
                  <option className="capitalize text-black" value={'hipster'}>
                    Hipster
                  </option>
                  <option className="capitalize text-black" value={'professional'}>
                    Professional
                  </option>
                  {customTheme && (
                    <option className="capitalize text-black" value={customTheme.id}>
                      Enterprise Custom Theme
                    </option>
                  )}
                </select>
              </div>
            </Card>
          )} */}

          {/* {isEdit && (
            <Card
              bgColor="blue"
              neon={neon}
              style={{ width: 'calc(50% - 30px)' }}
            >
              <p className="mb-4 text-sm text-white">
                As your Poll is already created, any changes made to questions
                or period will result in the Poll being reset. All current votes
                will be lost.
              </p>

              <div>
                <ButtonMighty
                  neon={neon}
                  color="#f97075"
                  text={`Modify Questions
                or Period`}
                  onClick={() => onNext()}
                  borderColor="#221f20"
                  margin="0"
                  textColor="#221f20"
                  width="auto"
                />
              </div>
            </Card>
          )} */}
        </div>

        {leftContent}

        {/* <div className="flex flex-col mx-4 space-y-4">
          {isEdit ? (
            <div>
              <ButtonMighty
                neon={neon}
                disabled={loading || isSubmitting}
                loading={loading || isSubmitting}
                color="#29FF2E"
                text="Save changes"
                onClick={() => onNext(true)}
                borderColor="#4400FF"
                margin="0"
                textColor="#4400FF"
              />
            </div>
          ) : (
            <div>
              <ButtonMighty
                neon={neon}
                disabled={loading || isSubmitting}
                color="#29FF2E"
                text="Continue"
                onClick={() => onNext(false)}
                borderColor="#4400FF"
                margin="0"
                textColor="#4400FF"
              />
            </div>
          )}
          <div>
            <ButtonMighty
              neon={neon}
              disabled={loading || isSubmitting}
              color="red"
              text="Cancel"
              onClick={() => history.goBack()}
              borderColor="#108EE9"
              margin="0"
              textColor="yellow"
            />
          </div>
        </div> */}
      </div>
    );
  }
  return (
    <div className="min-h-screen pt-16 pb-32 bg-black">
      <div className="container mx-auto">
        <ConfirmDialog
          loading={isSubmitting || purchaseModal.isSubmitting}
          className="text-white bg-gray-darkest shadow-r-salmon-md"
          title="Confirm purchase poll"
          content={
            <div className="flex flex-col mt-8 space-y-4">
              <PollPremiumQuotaForm
                onSuccess={() => {
                  setPurchaseModal({
                    isSubmitting: false,
                    visible: false,
                  });
                }}
              />
              <div>
                <ButtonMighty
                  disabled={loading || purchaseModal.isSubmitting}
                  color="#f00221"
                  text="Cancel"
                  onClick={
                    purchaseModal.onCancel
                      ? purchaseModal.onCancel
                      : () =>
                          setPurchaseModal({
                            isSubmitting: false,
                            visible: false,
                          })
                  }
                  borderColor="#000"
                  margin="0"
                  textColor="#000"
                  width="auto"
                />
              </div>
            </div>
          }
          open={purchaseModal.visible}
          onCancel={
            purchaseModal.onCancel
              ? purchaseModal.onCancel
              : () =>
                  setPurchaseModal({
                    isSubmitting: false,
                    visible: false,
                  })
          }
          onConfirm={purchaseModal.onConfirm}
        />
        {header}
        <div className="flex flex-col gap-8 lg:flex-row">
          {/* <div className="w-full lg:min-h-screen lg:sticky lg:top-0 lg:w-96">
            {leftContent}
          </div> */}
          <div className="flex flex-1">
            <div className="flex-1 pb-24 lg:mr-4">
              {content} {StepTwo}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PollCreateStepOne;
